import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

import type { BrandedHeaderData, HeaderDevice } from './BrandedHeader.types.js';
import { Flex } from '@repo/ui/Flex';
import { IconButton } from '@repo/ui/IconButton';
import { BrandedHeaderMenuContainerContext } from './BrandedHeaderMenuContainerContext.js';
import s from './BrandedHeader.module.css';

const MOBILE_DRAWER_CLOSE_MS = 180;

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

const isSafeHref = (href: string): boolean => {
  try {
    return ALLOWED_PROTOCOLS.includes(new URL(href).protocol);
  } catch {
    return ALLOWED_PROTOCOLS.some((p) => href.startsWith(p));
  }
};

interface CleanLink {
  text: string;
  url: string;
}

const cleanLinks = (links: BrandedHeaderData['navigationLinks']): CleanLink[] =>
  (links ?? []).reduce<CleanLink[]>((acc, link) => {
    const url = link?.url?.trim();
    const text = link?.text?.trim();
    if (url && text && isSafeHref(url)) {
      acc.push({ text, url });
    }
    return acc;
  }, []);

const LogoImage = ({
  logo,
}: {
  logo: NonNullable<BrandedHeaderData['logo']>;
}) => {
  const img = (
    <img className={s.logoImg} src={logo.src!} alt={logo.alt ?? ''} />
  );

  if (logo.href && isSafeHref(logo.href)) {
    return (
      <a
        className={s.logoLink}
        href={logo.href}
        rel="noopener noreferrer"
        target="_blank"
        aria-label="Home"
      >
        {img}
      </a>
    );
  }

  return img;
};

export interface BrandedHeaderProps {
  header: BrandedHeaderData;
  device?: HeaderDevice;
}

export const BrandedHeader = ({
  header,
  device = 'desktop',
}: BrandedHeaderProps) => {
  const layout = header.headerLayout ?? 'STACKED';
  const logoAlignment = header.headerLogoAlignment ?? 'CENTER';
  const navAlignment = header.headerNavAlignment ?? 'CENTER';
  const renderableLinks = cleanLinks(header.navigationLinks);
  const menuContainer = useContext(BrandedHeaderMenuContainerContext);
  const showMobileMenu = device === 'mobile';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const [headerBottom, setHeaderBottom] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const drawerId = useId();

  const focusFirstLink = useCallback((el: HTMLAnchorElement | null) => {
    el?.focus({ preventScroll: true });
  }, []);

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (open) {
      setDrawerClosing(false);
      setDrawerOpen(true);
      return;
    }
    setDrawerClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      setDrawerOpen(false);
      setDrawerClosing(false);
      closeTimeoutRef.current = null;
      toggleButtonRef.current?.focus();
    }, MOBILE_DRAWER_CLOSE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showMobileMenu) return;
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      setHeaderBottom(Math.max(0, el.offsetTop + el.offsetHeight));
    };
    update();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [showMobileMenu]);

  const hasLogo = header.logo?.src;
  if (!renderableLinks.length && !hasLogo) return null;

  const style: Record<string, string> = {};
  if (header.headerBackgroundColor) {
    style['--n-branded-header-bg-color'] = header.headerBackgroundColor;
  }
  if (header.headerTextColor) {
    style['--n-branded-header-text-color'] = header.headerTextColor;
  }

  return (
    <>
      <header
        ref={headerRef}
        className={s.header}
        style={style}
        data-fixed={header.headerFixed || undefined}
        data-testid="branded-header"
      >
        <div
          className={s.inner}
          data-layout={showMobileMenu ? 'MOBILE' : layout}
          data-logo-align={showMobileMenu ? 'CENTER' : logoAlignment}
          data-nav-align={showMobileMenu ? undefined : navAlignment}
        >
          {showMobileMenu && renderableLinks.length > 0 && (
            <Flex className={s.mobileMenuSlot} alignItems="center" shrink="0">
              <IconButton
                ref={toggleButtonRef}
                icon={drawerOpen ? 'close' : 'menu'}
                label={drawerOpen ? 'Close navigation menu' : 'Navigation menu'}
                variant="tertiary"
                size="sm"
                className={s.mobileMenuButton}
                aria-expanded={drawerOpen}
                aria-controls={drawerId}
                onClick={() => handleDrawerOpenChange(!drawerOpen)}
              />
            </Flex>
          )}
          {hasLogo && (
            <div className={s.logoSlot}>
              <LogoImage logo={header.logo!} />
            </div>
          )}
          {!showMobileMenu && renderableLinks.length > 0 && (
            <nav className={s.navSlot} aria-label="Header navigation">
              <ul className={s.navList}>
                {renderableLinks.map((link) => (
                  <li key={`${link.url}-${link.text}`} className={s.navItem}>
                    <a
                      className={s.navLink}
                      href={link.url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </header>
      {drawerOpen &&
        menuContainer &&
        createPortal(
          <>
            <div
              className={clsx(
                s.menuOverlay,
                drawerClosing && s.menuOverlayClosing,
              )}
              style={{ position: 'absolute', top: `${headerBottom}px` }}
              onClick={() => handleDrawerOpenChange(false)}
              aria-hidden
            />
            <div
              id={drawerId}
              className={clsx(
                s.portalDrawer,
                drawerClosing && s.portalDrawerClosing,
              )}
              style={{ top: `${headerBottom}px` }}
              role="dialog"
              aria-modal="true"
              aria-label="Header navigation"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleDrawerOpenChange(false);
                }
              }}
            >
              <nav
                aria-label="Header navigation"
                className={s.mobileMenuContent}
              >
                <Flex as="ul" direction="column" className={s.mobileMenuList}>
                  {renderableLinks.map((link, i) => (
                    <li
                      key={`${link.url}-${link.text}`}
                      className={s.mobileMenuItem}
                    >
                      <a
                        ref={i === 0 ? focusFirstLink : undefined}
                        className={s.mobileMenuLink}
                        href={link.url}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={() => handleDrawerOpenChange(false)}
                      >
                        {link.text}
                      </a>
                    </li>
                  ))}
                </Flex>
              </nav>
            </div>
          </>,
          menuContainer,
        )}
    </>
  );
};
